/**
 * So'z shakllari (demo «Lexo v3» `FormsHub`/`fStart`/`fFinish`/`fChain`/`fMatch`):
 * EN — noto'g'ri fe'llar (V1–V2–V3), RU — fe'l juftlari (НСВ – СВ). O'z Leitner jadvali — store `fw`.
 * Ichki fazalar: hub → practice (3 bosqich) → finish; chain (60 soniya), match (6 juft). Natija serverga (`/v1/forms/finish`, `/v1/games`).
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { fmtTime, fold, INTERVALS, LANG, LN, shuffle } from '../../data';
import { FKIND, FM, fJoin, type FormItem } from '../../data/forms';
import { t, tn } from '../../i18n';
import { nav, useNav, type Tab } from '../../lib/nav';
import { boop, chime, knowSound } from '../../lib/sound';
import { speak } from '../../lib/speech';
import { applyCourse, fire, sync } from '../../lib/sync';
import { haptic } from '../../lib/telegram';
import { toast } from '../../lib/toast';
import { estXp, XPR } from '../../lib/xp';
import { bump, S, today, useStore, type FormState } from '../../store';
import { Ic } from '../../ui/Icon';

type Phase = 'hub' | 'practice' | 'finish' | 'chain' | 'match';

/** Mashq qadami: 0 — tanishish (kartochka), 1 — shaklni tanlash, 2 — shaklni yozish; `h` — yashirilgan shakl indeksi. */
interface FStep { t: 'card' | 'pick' | 'write'; x: FormItem; stage: number; h: number; retry?: boolean }
/** Demo `FL`. */
interface Run {
  set: FormItem[]; known: string[]; steps: FStep[]; i: number; ok: number; tot: number; xp: number;
  res: Record<string, boolean>; due: string[]; built: boolean;
  answers: Array<{ id: string; ok: boolean }>;   // serverga — har bir javob tartib bilan
  fb: { ok: boolean; x: FormItem } | null;        // pastki varaq
}
interface Summary { id: number; xp: number; nN: number; nR: number; acc: number; set: FormItem[] }

const FSTG = ['Tanishish', 'Shaklni tanlang', 'Shaklni yozing'];
const isDue = (s: FormState | undefined) => !!s && s.due <= today();
const sayForms = (x: FormItem) => speak(x.forms.join(', '));
const short = (l: string) => l.split(' · ')[0]!;
/** O'yinlar uchun to'plam: boshlangan shakllar (kamida 5 ta), aks holda hammasi. */
const fPool = () => { const fw = S().fw; const l = FM.items.filter((x) => fw[x.id]); return l.length >= 5 ? l : FM.items; };

/* ---------- chalg'ituvchi variantlar ---------- */
/** «To'g'ri» qoida bo'yicha yasalgan xato shakl: goed, taked, buyed. */
function enRegular(v: string) {
  v = v.split('/')[0]!;
  if (v.length <= 2) return v + 'ed';
  if (/e$/.test(v)) return v + 'd';
  if (/[^aeiou]y$/.test(v)) return v.slice(0, -1) + 'ied';
  if (/^[^aeiou]*[aeiou][^aeiouwy]$/.test(v)) return v + v.slice(-1) + 'ed';
  return v + 'ed';
}
/** Aynan shu so'zning boshqa shakllari + qoida bo'yicha yasalgan xato shakllar (3 ta). */
function fDistract(x: FormItem, h: number): string[] {
  const right = x.forms[h]!, seen = new Set([right]), out: string[] = [];
  const add = (v: string | null) => { if (v && !seen.has(v)) { seen.add(v); out.push(v); } };
  if (LANG === 'ru') {
    const base = x.forms[0]!; add(h === 1 ? base : x.forms[1]!);
    shuffle(['по', 'на', 'с', 'про', 'вы', 'за'].filter((p) => !(p === 'с' && /^[аеёиоуыэюя]/.test(base))).map((p) => p + base).filter((v) => v !== right && v !== 'пописать')).forEach(add);
  } else {
    const v1 = x.forms[0]!.split('/')[0]!, v2 = x.forms[1]!.split('/')[0]!, v3 = x.forms[2]!;
    x.forms.forEach((f, i) => { if (i !== h) add(f); });                 // shu fe'lning boshqa shakllari
    add(enRegular(v1));                                                   // goed, taked, buyed
    add(h === 2 ? enRegular(v2) : v3.endsWith('en') ? null : (v1.endsWith('e') ? v1 + 'n' : v1 + 'en')); // wented / goen
    add(v2 + 'ed'); add(v1 + 's');
  }
  const own = out.filter((v) => v !== right).slice(0, 3);
  if (own.length < 3) shuffle(FM.items.filter((y) => y.id !== x.id)).forEach((y) => { const f = y.forms[h]!; if (own.length < 3 && !own.includes(f) && f !== right) own.push(f); });
  return own;
}

/* ---------- mashq oqimi ---------- */
const hidePick = (x: FormItem) => 1 + Math.floor(Math.random() * (x.forms.length - 1));
/** Demo `fBuild`: «Bilaman» belgilanmaganlar uchun 2- va 3-bosqichlar. */
function build(r: Run): Run {
  const L2 = r.set.filter((x) => !r.known.includes(x.id));
  return {
    ...r, built: true,
    steps: [...r.steps,
      ...shuffle(L2).map((x): FStep => ({ t: 'pick', x, stage: 1, h: hidePick(x) })),
      ...shuffle(L2).map((x): FStep => ({ t: 'write', x, stage: 2, h: hidePick(x) }))],
  };
}

export function Forms({ from }: { from: Tab }) {
  const [phase, setPhase] = useState<Phase>('hub');
  const [run, setRun] = useState<Run | null>(null);
  const [sum, setSum] = useState<Summary | null>(null);
  const [game, setGame] = useState(0); // «Yana» — o'yinni qayta yaratish kaliti

  const toHub = () => { setRun(null); setPhase('hub'); };
  const back = () => { if (useNav.getState().stack.length) nav.pop(); else nav.home(from); };

  /** Demo `fStart`: 4 tagacha takrorlash + jami 8 tagacha yangi. */
  const start = () => {
    const fw = S().fw;
    const due = shuffle(FM.items.filter((x) => isDue(fw[x.id]))).slice(0, 4);
    const nw = shuffle(FM.items.filter((x) => !fw[x.id])).slice(0, 8 - due.length);
    const set = [...due, ...nw];
    if (!set.length) { toast(t('Bugun takrorlash yoʻq')); return; }
    let r: Run = { set, known: [], steps: nw.map((x) => ({ t: 'card', x, stage: 0, h: -1 })), i: 0, ok: 0, tot: 0, xp: 0, res: {}, due: due.map((x) => x.id), built: false, answers: [], fb: null };
    if (!nw.length) r = build(r);
    setRun(r); setPhase('practice');
  };

  /** Demo `fFinish`: Leitner yangilanishi (lokal), XP taxmini, serverga yuborish. */
  const finish = (r: Run) => {
    const tt = today(), fw0 = S().fw, fw = { ...fw0 };
    let nN = 0, nR = 0;
    r.known.forEach((id) => { fw[id] = { box: 3, due: tt + INTERVALS[3] }; nN++; });
    Object.entries(r.res).forEach(([id, ok]) => {
      if (r.known.includes(id)) return;
      const isNew = !fw0[id]; const x = { ...(fw0[id] ?? { box: 0, due: tt }) };
      if (isNew) { x.box = 1; x.due = tt + INTERVALS[1]; nN++; }
      else { nR++; if (ok) { x.box = Math.min(6, x.box + 1); x.due = tt + INTERVALS[x.box]!; } else { x.box = 1; x.due = tt + 1; } }
      fw[id] = x;
    });
    S().set({ fw });
    const xp = estXp(r.xp + nN * XPR.newWord + (r.tot && r.ok === r.tot ? XPR.allRight : 0));
    bump(xp);
    const id = Date.now();
    setSum({ id, xp, nN, nR, acc: r.tot ? Math.round(r.ok / r.tot * 100) : 100, set: r.set });
    setRun(null); setPhase('finish');
    // Server — haqiqat manbai: qutilar, kurs holati va haqiqiy XP
    fire(sync.forms({ known: [...r.known], answers: r.answers }).then((res) => {
      S().set({ fw: { ...S().fw, ...res.forms } });
      applyCourse(res.course);
      setSum((s) => (s && s.id === id ? { ...s, xp: res.xp } : s));
    }), 'forms');
  };

  if (phase === 'practice' && run) return <Practice run={run} setRun={setRun} onFinish={finish} onQuit={toHub} />;
  if (phase === 'finish' && sum) return <FinishView sum={sum} onHub={toHub} />;
  if (phase === 'chain') return <Chain key={game} onAgain={() => setGame(game + 1)} onHub={toHub} />;
  if (phase === 'match') return <FMatch key={game} onAgain={() => setGame(game + 1)} onHub={toHub} />;
  return <Hub onBack={back} onStart={start} onChain={() => setPhase('chain')} onMatch={() => setPhase('match')} />;
}

/* ---------- hub ---------- */
function Hub({ onBack, onStart, onChain, onMatch }: { onBack: () => void; onStart: () => void; onChain: () => void; onMatch: () => void }) {
  const fw = useStore((s) => s.fw);
  const due = FM.items.filter((x) => isDue(fw[x.id])).length;
  const nw = FM.items.filter((x) => !fw[x.id]).length;
  const learned = FM.items.filter((x) => (fw[x.id]?.box ?? 0) >= 3).length;
  return (
    <>
      <div className="sbar">
        <button className="x" id="quit" aria-label={t('Orqaga')} onClick={onBack}><Ic n="back" /></button>
        <b style={{ flex: 1, textAlign: 'left', color: 'var(--ink)', fontSize: 16 }}>{t('Soʻz shakllari')}</b>
      </div>
      <div className="sbody fade" style={{ gap: 14 }}>
        <section className="fhero">
          <span className="fbadge">{LN.code}</span>
          <div className="col g4"><b style={{ fontSize: 21, lineHeight: '26px' }}>{t(FM.title)}</b><span style={{ fontSize: 13, fontWeight: 600, opacity: 0.9 }}>{t(FM.sub)}</span></div>
          <div className="fstats">
            <div><b>{learned}/{FM.items.length}</b><span>{t('yodlangan')}</span></div>
            <div><b>{due}</b><span>{t('takrorlash')}</span></div>
            <div><b>{nw}</b><span>{t('yangi')}</span></div>
          </div>
          <button className="cta2" id="fgo" disabled={!(due + nw)} onClick={onStart}>
            {due + nw ? t('Mashqni boshlash') : t('Bugun tugadi')} <Ic n="play" style={{ width: 14, height: 14, fill: 'currentColor' }} />
          </button>
        </section>
        <div className="row g10">
          <button className="qa2" id="fchain" style={{ flex: 1 }} onClick={onChain}>
            <span className="sq2" style={{ background: 'var(--peach-soft)', color: 'var(--peach-ink)' }}><Ic n="timer" /></span>
            <span className="grow col g4" style={{ textAlign: 'left' }}><b className="t15b">{t('Shakl zanjiri')}</b><span className="t13">{tn('# soniya', 60)}</span></span>
          </button>
          <button className="qa2" id="fmatch" style={{ flex: 1 }} onClick={onMatch}>
            <span className="sq2" style={{ background: 'var(--info-soft)', color: 'var(--info-ink)' }}><Ic n="grid" /></span>
            <span className="grow col g4" style={{ textAlign: 'left' }}><b className="t15b">{t('Juftlik')}</b><span className="t13">{tn('# juft', 6)}</span></span>
          </button>
        </div>
        <div className="between"><b className="t16b">{tn('Roʻyxat · #', FM.items.length)}</b><span className="t12">{FM.labels.map(t).join(' · ')}</span></div>
        <div className="wl">
          {FM.items.map((x) => {
            const b = fw[x.id]?.box ?? 0;
            return (
              <div key={x.id} className="wr">
                <button className="spk" aria-label={t('Tinglash')} onClick={() => sayForms(x)}><Ic n="vol" /></button>
                <div className="grow"><b>{fJoin(x)}</b><span>{x.uz}{x.kind ? ` · ${t(FKIND[x.kind])}` : ''}</span></div>
                <div className="lvl">{[1, 2, 3, 4, 5, 6].map((i) => <i key={i} className={i <= b ? 'on' : ''} />)}</div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

/* ---------- mashq ---------- */
/** Demo `fTable`: shakllar jadvali, `hide` — yashirilgan katak («?»). */
function FTable({ x, hide }: { x: FormItem; hide: number }) {
  return (
    <div className="ftab">
      {x.forms.map((f, i) => <div key={i} className={i === hide ? 'hid' : ''}><span>{t(FM.labels[i]!)}</span><b>{i === hide ? '?' : f}</b></div>)}
    </div>
  );
}

function Prompt({ retry, text }: { retry?: boolean; text: string }) {
  return <p className="t15b" style={{ color: 'var(--muted)', margin: 0 }}>{retry ? t('Yana bir bor: ') : ''}{t(text)}</p>;
}

function Practice({ run, setRun, onFinish, onQuit }: { run: Run; setRun: (r: Run) => void; onFinish: (r: Run) => void; onQuit: () => void }) {
  const s = run.steps[run.i]!;
  const stg = s.stage;
  const idx = run.steps.map((x, i) => (x.stage === stg ? i : -1)).filter((i) => i >= 0);
  const pos = Math.max(0, idx.indexOf(run.i));

  /** Demo `fMark`: javobni yozish; xato bo'lsa shu bosqich oxiriga qaytadi. */
  const mark = (ok: boolean) => {
    const x = s.x, r: Run = { ...run, tot: run.tot + 1, res: { ...run.res, [x.id]: (run.res[x.id] ?? true) && ok }, answers: [...run.answers, { id: x.id, ok }], fb: { ok, x } };
    if (ok) { r.ok++; r.xp += r.due.includes(x.id) ? XPR.review(S().fw[x.id]?.box ?? 1) : XPR.lessonAns; }
    else {
      let e = run.i; while (e + 1 < run.steps.length && run.steps[e + 1]!.stage === s.stage) e++;
      r.steps = [...run.steps]; r.steps.splice(e + 1, 0, { ...s, retry: true });
    }
    const st = S(); st.set({ right: st.right + (ok ? 1 : 0), wrong: st.wrong + (ok ? 0 : 1) });
    if (ok) { haptic.ok(); chime(); } else { haptic.bad(); boop(); }
    setRun(r);
  };
  /** Demo `fNext`. */
  const next = () => {
    let r: Run = { ...run, fb: null, i: run.i + 1 };
    if (!r.built && r.i >= r.steps.length) r = build(r);
    if (r.i >= r.steps.length) onFinish(r); else setRun(r);
  };
  const prev = () => { if (run.i > 0) setRun({ ...run, i: run.i - 1 }); };
  const toggleKnown = (id: string) => {
    const on = !run.known.includes(id); knowSound(on);
    setRun({ ...run, known: on ? [...run.known, id] : run.known.filter((k) => k !== id) });
  };

  const p = idx.length ? Math.round(pos / idx.length * 100) : 0;
  return (
    <>
      <div className="sbar">
        <button className="x" id="quit" aria-label={t('Chiqish')} onClick={onQuit}><Ic n="x" /></button>
        <div className="trk"><i style={{ width: `${p}%` }} /></div>
        <b>{pos + 1}/{idx.length}</b>
      </div>
      <div className="stg">
        <div className="dots">{FSTG.map((_, i) => <i key={i} className={i < stg ? 'done' : i === stg ? 'on' : ''} />)}</div>
        <span>{stg + 1}/3 · {t(FSTG[stg]!)}</span>
      </div>
      {s.t === 'card'
        ? <CardStep key={run.i} run={run} s={s} onPrev={prev} onNext={next} onKnow={toggleKnown} />
        : s.t === 'pick'
          ? <PickStep key={run.i} s={s} fb={run.fb} onMark={mark} onNext={next} />
          : <WriteStep key={run.i} s={s} fb={run.fb} onMark={mark} onNext={next} />}
    </>
  );
}

/** Demo `fSheet`: to'g'ri/xato varag'i. */
function Sheet({ fb, onNext }: { fb: NonNullable<Run['fb']>; onNext: () => void }) {
  const btn = useRef<HTMLButtonElement>(null);
  useEffect(() => { btn.current?.focus(); }, []);
  return (
    <div className={`sheet ${fb.ok ? 'ok' : 'bad'}`}>
      <div className="hd">
        <i><Ic n={fb.ok ? 'check' : 'x'} style={{ width: 22, height: 22, strokeWidth: 2.6 }} /></i>
        <b>{fb.ok ? t('Barakalla!') : t('Toʻgʻri javob:')}</b>
      </div>
      <div className="ans" style={{ fontSize: 20, lineHeight: '28px' }}>{fJoin(fb.x)}</div>
      {!fb.ok && <p className="msg">{t('Bu shakl shu bosqich oxirida yana chiqadi.')}</p>}
      <button className="btn" id="cont" ref={btn} onClick={onNext}>{fb.ok ? t('Davom etish') : t('Tushunarli')}</button>
    </div>
  );
}

/** Demo `sess` ramkasi: `.sbody` + `#ft` (varaq yoki qadam tugmalari). */
function Frame({ children, foot, fb, onNext }: { children: ReactNode; foot?: ReactNode; fb?: Run['fb']; onNext?: () => void }) {
  return (
    <>
      <div className="sbody fade" id="sb">{children}</div>
      <div id="ft">{fb && onNext ? <Sheet fb={fb} onNext={onNext} /> : foot ? <div className="sfoot">{foot}</div> : null}</div>
    </>
  );
}

function CardStep({ run, s, onPrev, onNext, onKnow }: { run: Run; s: FStep; onPrev: () => void; onNext: () => void; onKnow: (id: string) => void }) {
  const x = s.x;
  const cards = run.steps.filter((y) => y.t === 'card'), idx = cards.indexOf(s), n = cards.length, known = run.known.includes(x.id), last = idx === n - 1;
  useEffect(() => { sayForms(x); }, [x.id]); // eslint-disable-line
  return (
    <Frame foot={<>
      <button className="btn soft sq" id="pv" aria-label={t('Oldingi')} disabled={idx === 0} onClick={onPrev}><Ic n="back" /></button>
      <button className="btn soft" style={{ flex: '1 1 0', minWidth: 0 }} id="know" aria-pressed={known} {...(known ? { 'data-k': '1' } : {})} onClick={() => onKnow(x.id)}>{known ? t('Bilaman ✓') : t('Bilaman')}</button>
      {last
        ? <button className="btn" id="fw" style={{ flex: '2 1 0', minWidth: 0, whiteSpace: 'nowrap', fontSize: 15 }} onClick={onNext}>{t('Tekshirishni boshlash')}</button>
        : <button className="btn sq" id="fw" aria-label={t('Keyingi')} onClick={onNext}><Ic n="back" style={{ transform: 'scaleX(-1)' }} /></button>}
    </>}>
      <div className="between">
        <span className="tag">{tn('YANGI SHAKL · #/#', idx + 1, n)}</span>
        {x.kind && <span className="t12" style={{ fontWeight: 700 }}>{t(FKIND[x.kind])}</span>}
      </div>
      <div className="qcard" style={{ gap: 14, padding: 20 }}>
        <div className="between">
          <b style={{ fontSize: 24 }}>{x.uz}</b>
          <button className="spk" id="au" aria-label={t('Tinglash')} onClick={() => sayForms(x)}><Ic n="vol" /></button>
        </div>
        <FTable x={x} hide={-1} />
        <span className="t13">{LANG === 'ru'
          ? t('НСВ — jarayon, takrorlanish (“qilardim”); СВ — bir martalik natija (“qilib boʻldim”).')
          : t('V2 — oʻtgan zamon (Past Simple), V3 — have/has + V3 (Present Perfect).')}</span>
      </div>
    </Frame>
  );
}

function PickStep({ s, fb, onMark, onNext }: { s: FStep; fb: Run['fb']; onMark: (ok: boolean) => void; onNext: () => void }) {
  const x = s.x, right = x.forms[s.h]!;
  const [opts] = useState(() => shuffle([right, ...fDistract(x, s.h)]));
  const [picked, setPicked] = useState<number | null>(null);
  const pick = (i: number) => { if (picked !== null) return; setPicked(i); onMark(opts[i] === right); };
  return (
    <Frame fb={fb} onNext={onNext}>
      <Prompt retry={s.retry} text="Tushib qolgan shaklni tanlang" />
      <div className="qcard" style={{ gap: 12 }}><b style={{ fontSize: 18 }}>{x.uz}</b><FTable x={x} hide={s.h} /></div>
      <div className="opts">
        {opts.map((o, i) => {
          const isC = o === right, done = picked !== null;
          const cls = ['opt', done && isC ? 'ok' : '', done && !isC && i === picked ? 'bad' : '', done && !isC && i !== picked ? 'dim' : ''].filter(Boolean).join(' ');
          return <button key={o} className={cls} disabled={done} onClick={() => pick(i)}><span>{o}</span>{done && isC && <Ic n="check" />}</button>;
        })}
      </div>
    </Frame>
  );
}

const clean = (v: string, n: number) => [...v.replace(/[^A-Za-zА-Яа-яЁё' -]/g, '').toLowerCase()].slice(0, n).join('');

/** Demo `fStep` → write: kataklar + yashirin input (darsdagi `WriteStep` kabi), aniq `fold` tekshiruvi. */
function WriteStep({ s, fb, onMark, onNext }: { s: FStep; fb: Run['fb']; onMark: (ok: boolean) => void; onNext: () => void }) {
  const x = s.x, target = x.forms[s.h]!.split('/')[0]!, chars = [...target], n = chars.length, tt = fold(target);
  const [val, setVal] = useState('');
  const [focus, setFocus] = useState(false);
  const [done, setDone] = useState<string | null>(null); // tekshirilgan (fold) javob
  const inp = useRef<HTMLInputElement>(null);
  useEffect(() => { const a = setTimeout(() => inp.current?.focus(), 60); return () => clearTimeout(a); }, []);

  const v = [...val], cur = Math.min(v.length, n - 1);
  const check = () => {
    if (done !== null || v.length < n) return;
    const a = fold(val);
    setDone(a); inp.current?.blur(); onMark(a === tt);
  };
  return (
    <Frame fb={fb} onNext={onNext} foot={<button className="btn" id="ck" disabled={v.length < n} onClick={check}>{t('Tekshirish')}</button>}>
      <Prompt retry={s.retry} text="Shaklni yozing" />
      <div className="qcard" style={{ gap: 12 }}><b style={{ fontSize: 18 }}>{x.uz}</b><FTable x={x} hide={s.h} /></div>
      <div className="col g8" style={{ alignItems: 'center', textAlign: 'center' }}>
        <span className="t13" style={{ fontWeight: 700 }}>{t(FM.labels[s.h]!)} · {tn('# ta harf', n)}</span>
        <label className="cells" id="cells" htmlFor="in">
          {chars.map((c, i) => c === ' '
            ? <span key={i} className="gap" />
            : <span key={i} className={['cell', done !== null ? (done[i] === tt[i] ? 'good' : 'wrong') : (focus && i === cur ? 'cur' : ''), v[i] ? 'fill' : ''].filter(Boolean).join(' ')} data-i={i}>{v[i] || ''}</span>)}
          <input id="in" ref={inp} value={val} maxLength={n} readOnly={done !== null} autoComplete="off" autoCapitalize="off" autoCorrect="off" spellCheck={false}
            aria-label={tn('Javob, # ta harf', n)}
            onChange={(e) => setVal(clean(e.target.value, n))}
            onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
            onKeyDown={(e) => { if (e.key === 'Enter') { if (done !== null) onNext(); else check(); } }} />
        </label>
      </div>
    </Frame>
  );
}

/* ---------- yakun ---------- */
function FinishView({ sum, onHub }: { sum: Summary; onHub: () => void }) {
  const streak = useStore((s) => s.streak);
  return (
    <>
      <div className="content fade" style={{ paddingTop: 32, gap: 18 }}>
        <div className="col g12" style={{ alignItems: 'center', textAlign: 'center' }}>
          <div className="flamebig"><Ic n="flame" /></div>
          <h1 className="h2">{t('Mashq tugadi!')}</h1>
          <span className="soon" style={{ fontSize: 15, padding: '6px 14px' }}>{tn('# kunlik streak', streak)}</span>
        </div>
        <div className="stat3 center">
          <div><b style={{ color: 'var(--primary-ink)' }}>+{sum.xp}</b><span>XP</span></div>
          <div><b>{sum.nN} + {sum.nR}</b><span>{t('yangi + takror')}</span></div>
          <div><b style={{ color: 'var(--success-ink)' }}>{sum.acc}%</b><span>{t('aniqlik')}</span></div>
        </div>
        <div className="learned">
          <div className="cap">{t('Mashq qilingan shakllar')}</div>
          {sum.set.map((x) => <div key={x.id} className="between"><b>{fJoin(x)}</b><span>{x.uz}</span></div>)}
        </div>
      </div>
      <div className="sfoot"><button className="btn" id="hm" onClick={onHub}>{t('Soʻz shakllariga')}</button></div>
    </>
  );
}

/* ---------- Shakl zanjiri: 60 soniyada jadvaldagi bo'sh shakllarni to'ldirish ---------- */
function Chain({ onAgain, onHub }: { onAgain: () => void; onHub: () => void }) {
  const [rows] = useState(() => shuffle(fPool()).slice(0, 5));
  const [hide] = useState(() => rows.map((x) => (x.forms.length === 3 ? [1, 2] : [Math.random() < 0.5 ? 0 : 1])));
  const [vals, setVals] = useState<Record<string, string>>({});
  const [left, setLeft] = useState(60);
  const [res, setRes] = useState<Record<string, boolean> | null>(null);
  const [end, setEnd] = useState<{ ok: number; tot: number; xp: number } | null>(null);
  const valsR = useRef(vals);
  useEffect(() => { valsR.current = vals; });
  const doneR = useRef(false);
  const inputs = useRef<HTMLInputElement[]>([]);
  const tmR = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const keys = rows.flatMap((_, r) => hide[r]!.map((i) => `${r}:${i}`));

  const check = () => {
    if (doneR.current) return; doneR.current = true; clearInterval(tmR.current);
    let ok = 0; const out: Record<string, boolean> = {};
    keys.forEach((k) => {
      const [r, i] = k.split(':').map(Number) as [number, number];
      const want = rows[r]!.forms[i]!, got = (valsR.current[k] ?? '').trim();
      const good = want.split('/').some((w) => fold(w) === fold(got));
      out[k] = good; if (good) ok++;
    });
    const xp = estXp(Math.min(XPR.gameCap, ok * 4));
    bump(xp);
    if (ok === keys.length) chime(); else boop();
    setRes(out); setEnd({ ok, tot: keys.length, xp });
    fire(sync.game({ game: 'forms_chain', score: ok }).then((r) => {
      applyCourse(r.course);
      setEnd((e) => (e ? { ...e, xp: r.xp } : e));
    }), 'game');
  };
  const checkR = useRef(check);
  useEffect(() => { checkR.current = check; });

  useEffect(() => {
    const t0 = Date.now();
    tmR.current = setInterval(() => {
      const l = Math.max(0, 60 - Math.floor((Date.now() - t0) / 1000)); setLeft(l);
      if (l <= 0) checkR.current();
    }, 250);
    const f = setTimeout(() => inputs.current[0]?.focus(), 80);
    return () => { clearInterval(tmR.current); clearTimeout(f); };
  }, []);

  let k = -1;
  return (
    <>
      <div className="sbar">
        <button className="x" id="quit" aria-label={t('Chiqish')} onClick={onHub}><Ic n="x" /></button>
        <b style={{ flex: 1, textAlign: 'left', color: 'var(--ink)', fontSize: 16 }}>{t('Shakl zanjiri')}</b>
        <b id="ct" style={{ color: 'var(--primary-ink)' }}>{fmtTime(left)}</b>
      </div>
      <div className="sbody fade" style={{ gap: 12 }}>
        <span className="t13" style={{ fontWeight: 700 }}>{t('Boʻsh kataklarni toʻldiring')}</span>
        <div className="chain">
          <div className="chh">{FM.labels.map((l) => <span key={l}>{t(short(l))}</span>)}</div>
          {rows.map((x, r) => (
            <div key={x.id} className="chr">
              <small>{x.uz}</small>
              <div className="chc">
                {x.forms.map((f, i) => {
                  if (!hide[r]!.includes(i)) return <b key={i}>{f}</b>;
                  const key = `${r}:${i}`, n = ++k, good = res?.[key];
                  return (
                    <input key={i} ref={(el) => { if (el) inputs.current[n] = el; }}
                      className={res ? (good ? 'good' : 'bad') : undefined}
                      value={res && !good ? f : (vals[key] ?? '')} readOnly={!!res}
                      autoComplete="off" autoCapitalize="off" autoCorrect="off" spellCheck={false} aria-label={`${x.uz} ${t(FM.labels[i]!)}`}
                      onChange={(e) => setVals({ ...vals, [key]: e.target.value })}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); const nx = inputs.current[n + 1]; if (nx) nx.focus(); else check(); } }} />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="sfoot">
        {end ? (
          <div className="between" style={{ width: '100%' }}>
            <b className="t15b">{tn('#/# toʻgʻri', end.ok, end.tot)} · +{end.xp} XP</b>
            <span className="row g8">
              <button className="btn soft" id="ag" style={{ height: 44, width: 'auto', padding: '0 14px' }} onClick={onAgain}>{t('Yana')}</button>
              <button className="btn" id="bk" style={{ height: 44, width: 'auto', padding: '0 14px' }} onClick={onHub}>{t('Tayyor')}</button>
            </span>
          </div>
        ) : <button className="btn" id="cc" onClick={check}>{t('Tekshirish')}</button>}
      </div>
    </>
  );
}

/* ---------- Juftlik: shakllarni juftlash (V1 ↔ V2/V3 yoki НСВ ↔ СВ) ---------- */
type Side = 'l' | 'r';
function FMatch({ onAgain, onHub }: { onAgain: () => void; onHub: () => void }) {
  const [set] = useState(() => shuffle(fPool()).slice(0, 6));
  const [b] = useState(() => (FM.labels.length === 3 ? (Math.random() < 0.5 ? 1 : 2) : 1));
  const [A] = useState(() => shuffle(set));
  const [B] = useState(() => shuffle(set));
  const [sel, setSel] = useState<{ id: string; side: Side } | null>(null);
  const [done, setDone] = useState<string[]>([]);
  const [bad, setBad] = useState<string[]>([]);   // 450ms qizil juft
  const [miss, setMiss] = useState(0);
  const [end, setEnd] = useState<{ xp: number; secs: number } | null>(null);
  const [t0] = useState(() => Date.now());
  const badTm = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(badTm.current), []);

  const click = (x: FormItem, side: Side) => {
    if (end || done.includes(x.id)) return;
    if (!sel || sel.side === side) { setSel({ id: x.id, side }); return; }
    if (sel.id === x.id) {
      const nd = [...done, x.id]; setDone(nd); setSel(null); haptic.ok();
      if (nd.length === set.length) {
        const xp = estXp(Math.min(XPR.gameCap, Math.max(5, 30 - miss * 5)));
        bump(xp); chime();
        setEnd({ xp, secs: Math.round((Date.now() - t0) / 1000) });
        fire(sync.game({ game: 'forms_match', score: 0, mistakes: miss }).then((r) => {
          applyCourse(r.course);
          setEnd((e) => (e ? { ...e, xp: r.xp } : e));
        }), 'game');
      }
    } else {
      setMiss(miss + 1); haptic.bad(); boop();
      setBad([`${sel.side}:${sel.id}`, `${side}:${x.id}`]); setSel(null);
      clearTimeout(badTm.current); badTm.current = setTimeout(() => setBad([]), 450);
    }
  };
  const cls = (x: FormItem, side: Side) => {
    const k = `${side}:${x.id}`;
    return ['m', sel && sel.side === side && sel.id === x.id ? 'sel' : '', done.includes(x.id) ? 'done' : '', bad.includes(k) ? 'bad' : ''].filter(Boolean).join(' ');
  };

  return (
    <>
      <div className="sbar">
        <button className="x" id="quit" aria-label={t('Chiqish')} onClick={onHub}><Ic n="x" /></button>
        <b style={{ flex: 1, textAlign: 'left', color: 'var(--ink)', fontSize: 16 }}>{t('Juftlik')} · {t(short(FM.labels[0]!))} ↔ {t(short(FM.labels[b]!))}</b>
      </div>
      <div className="sbody fade" style={{ gap: 12 }}>
        <div className="match">
          <div>{A.map((x) => <button key={x.id} className={cls(x, 'l')} data-id={x.id} data-s="l" onClick={() => click(x, 'l')}>{x.forms[0]}</button>)}</div>
          <div>{B.map((x) => <button key={x.id} className={cls(x, 'r')} data-id={x.id} data-s="r" onClick={() => click(x, 'r')}>{x.forms[b]}</button>)}</div>
        </div>
      </div>
      <div id="ft">
        {end && (
          <div className="sheet ok">
            <div className="hd"><i><Ic n="check" style={{ width: 22, height: 22, strokeWidth: 2.6 }} /></i><b>{t('Hammasi topildi!')}</b><span className="xp">+{end.xp} XP</span></div>
            <p className="msg" style={{ color: 'var(--success-ink)' }}>{fmtTime(end.secs)} · {miss ? tn('# ta xato', miss) : t('Xatosiz')}</p>
            <div className="row g10">
              <button className="btn soft" id="ag" style={{ background: '#fff', color: 'var(--success-ink)' }} onClick={onAgain}>{t('Yana oʻynash')}</button>
              <button className="btn" id="cont" autoFocus onClick={onHub}>{t('Tayyor')}</button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
